import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
} from "@mui/material";
import { PanelLeftClose, Trash2, CirclePlus, ChevronRight, ChevronDown } from "lucide-mui";
import TabContentOverlay from "./TabContentOverlay";
import { useTheme } from "../context/ThemeContext";

const parseInitialSkeleton = s => ({
  title: s?.title ?? "",
  phases: (s?.phases ?? []).map(phase => ({
    ...phase,
    phase_outline: phase.phase_outline ?? [],
  })),
});

export default function EditProcedureModal({
  initialSkeleton,
  isProcedureModalOpen,
  setIsProcedureModalOpen,
  currentAnchorId,
  onCancel,
  onSave,
  onToggleSidebar,
}) {
  const [procedureSkeleton, setProcedureSkeleton] = useState(() =>
    parseInitialSkeleton(initialSkeleton),
  );
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingPhase, setEditingPhase] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [tempValue, setTempValue] = useState("");
  const [expandedAccordions, setExpandedAccordions] = useState(new Set([]));
  const [editingPhaseName, setEditingPhaseName] = useState(null);
  const scrollContainerRef = useRef(null);
  const lastAccordionRef = useRef(null);
  // Capture the skeleton as it was when the modal opened, so Cancel always reverts to that state
  // regardless of whether the parent re-renders with an updated initialSkeleton prop.
  const initialSkeletonRef = useRef(parseInitialSkeleton(initialSkeleton));
  const { isDark } = useTheme();

  const resetEditingState = useCallback(() => {
    setProcedureSkeleton(initialSkeletonRef.current);
    setEditingTitle(false);
    setEditingPhase(null);
    setEditingItem(null);
    setEditingPhaseName(null);
    setTempValue("");
    setExpandedAccordions(new Set());
    onCancel?.();
  }, [onCancel]);

  const addPhase = useCallback(() => {
    setProcedureSkeleton(prev => ({
      ...prev,
      phases: [
        ...prev.phases,
        { name: "", phase_summary: "", phase_outline: [""] },
      ],
    }));
  }, []);

  useEffect(() => {
    if (lastAccordionRef.current && scrollContainerRef.current) {
      lastAccordionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [procedureSkeleton.phases.length]);

  const deletePhase = useCallback(index => {
    setProcedureSkeleton(prev => ({
      ...prev,
      phases: prev.phases.filter((_, i) => i !== index),
    }));
  }, []);

  const addItem = useCallback(phaseIndex => {
    setProcedureSkeleton(prev => {
      const newPhases = [...prev.phases];
      newPhases[phaseIndex] = {
        ...newPhases[phaseIndex],
        phase_outline: [...newPhases[phaseIndex].phase_outline, ""],
      };
      return { ...prev, phases: newPhases };
    });
  }, []);

  const deleteItem = useCallback((phaseIndex, itemIndex) => {
    setProcedureSkeleton(prev => {
      const newPhases = [...prev.phases];
      newPhases[phaseIndex] = {
        ...newPhases[phaseIndex],
        phase_outline: newPhases[phaseIndex].phase_outline.filter(
          (_, i) => i !== itemIndex,
        ),
      };
      return { ...prev, phases: newPhases };
    });
  }, []);

  const updatePhase = useCallback((index, field, value) => {
    setProcedureSkeleton(prev => {
      const newPhases = [...prev.phases];
      newPhases[index] = { ...newPhases[index], [field]: value };
      return { ...prev, phases: newPhases };
    });
  }, []);

  const updateItem = useCallback((phaseIndex, itemIndex, value) => {
    setProcedureSkeleton(prev => {
      const newPhases = [...prev.phases];
      newPhases[phaseIndex] = {
        ...newPhases[phaseIndex],
        phase_outline: [...newPhases[phaseIndex].phase_outline],
      };
      newPhases[phaseIndex].phase_outline[itemIndex] = value;
      return { ...prev, phases: newPhases };
    });
  }, []);

  const addBtnSx = {
    height: "36px",
    color: isDark ? "#ECEFF3" : "#434950",
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    letterSpacing: "0.021px",
    textTransform: "none",
    background: isDark
      ? "linear-gradient(165.17deg, rgba(67,73,80,0.8) 0%, rgba(49,54,59,0.8) 50%, rgba(49,54,59,0.8) 100%)"
      : "linear-gradient(164.96deg, rgba(248,249,250,0.8) 0%, rgba(226,231,235,0.9) 50%, rgb(211,218,225) 100%)",
    border: "1px solid",
    borderColor: isDark ? "rgba(106,112,121,0.5)" : "rgba(202,203,205,0.4)",
    borderRadius: "12px",
    padding: "8px 16px",
    gap: "8px",
    "&:hover": {
      background: isDark
        ? "linear-gradient(165.17deg, rgba(80,87,96,0.9) 0%, rgba(60,66,72,0.9) 100%)"
        : "linear-gradient(164.96deg, rgba(236,239,244,0.9) 0%, rgba(210,217,223,1) 50%, rgb(195,204,213) 100%)",
    },
  };

  return (
    <TabContentOverlay
      isOpen={isProcedureModalOpen}
      onClose={() => setIsProcedureModalOpen(false)}
      anchorId={currentAnchorId}
    >
      <Box
        sx={{
          bgcolor: isDark ? "#16181A" : "#FCFDFF",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          pt: "8px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              height: "70px",
              background: isDark ? "#16181A" : "#FCFDFF",
              display: "flex",
              alignItems: "center",
              px: "24px",
              gap: "16px",
              flexShrink: 0,
            }}
          >
            {onToggleSidebar && (
              <IconButton
                onClick={onToggleSidebar}
                size='small'
                sx={{
                  flexShrink: 0,
                  width: "24px",
                  height: "24px",
                  color: isDark ? "#D5D9DF" : "#6A7079",
                  "&:hover": {
                    color: isDark ? "#D5D9DF" : "#3D424A",
                    backgroundColor: "transparent",
                  },
                  "& svg": { width: "24px", height: "24px" },
                }}
              >
                <PanelLeftClose sx={{ transform: "rotate(180deg)" }} />
              </IconButton>
            )}
            {editingTitle ? (
              <TextField
                fullWidth
                value={procedureSkeleton.title}
                onChange={e =>
                  setProcedureSkeleton(prev => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }
                onBlur={() => setEditingTitle(false)}
                autoFocus
                variant='outlined'
                size='small'
                sx={{
                  height: "44px",
                  "& .MuiOutlinedInput-root": {
                    height: "44px",
                    color: isDark ? "#ECEFF3" : "#434950",
                    fontFamily: "Inter",
                    fontSize: "24px",
                    fontWeight: 500,
                    lineHeight: "28px",
                    letterSpacing: "0.036px",
                    background: isDark ? "#2D2F31" : "#FFFFFF",
                    borderRadius: "10px",
                    "& fieldset": {
                      borderColor: isDark ? "#555555" : "#D5D9DF",
                      borderWidth: "1px",
                    },
                    "&:hover fieldset": {
                      borderColor: isDark ? "#555555" : "#D5D9DF",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: isDark ? "#555555" : "#D5D9DF",
                      borderWidth: "1px",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    fontSize: "24px",
                    fontFamily: "Inter",
                    fontWeight: 500,
                    lineHeight: "28px",
                    letterSpacing: "0.036px",
                    padding: "8px 10px",
                  },
                }}
              />
            ) : (
              <Typography
                onClick={() => setEditingTitle(true)}
                sx={{
                  flex: 1,
                  color: isDark ? "#ECEFF3" : "#434950",
                  fontFamily: "Inter",
                  fontSize: "24px",
                  fontWeight: 500,
                  lineHeight: "28px",
                  letterSpacing: "0.036px",
                  cursor: "pointer",
                  height: "44px",
                  borderRadius: "10px",
                  "&:hover": {
                    bgcolor: isDark ? "#2D2F31" : "#F3F6FA",
                  },
                  display: "flex",
                  alignItems: "center",
                  px: "10px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {procedureSkeleton.title || (
                  <span style={{ fontStyle: "italic", opacity: 0.4 }}>
                    Title
                  </span>
                )}
              </Typography>
            )}
          </Box>

          {/* Scrollable Content */}
          <Box
            ref={scrollContainerRef}
            sx={{
              flex: 1,
              overflow: "auto",
              p: "16px",
              m: "24px",
              mt: "0",
              maxWidth: "100%",
              overflowX: "hidden",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              borderRadius: "8px",
              border: "1px solid",
              borderColor: isDark ? "#31363B" : "#D5D9DF33",
              background: isDark ? "#1A1C1E" : "#FFFFFF",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {procedureSkeleton.phases.map((phase, phaseIndex) => {
              const isExpanded = expandedAccordions.has(phaseIndex);
              const isLastAccordion =
                phaseIndex === procedureSkeleton.phases.length - 1;
              return (
                <Accordion
                  key={phaseIndex}
                  ref={isLastAccordion ? lastAccordionRef : null}
                  expanded={isExpanded}
                  onChange={() => {
                    const tag = document.activeElement?.tagName;
                    if (tag === "INPUT" || tag === "TEXTAREA") return;
                    const newExpanded = new Set(expandedAccordions);
                    if (isExpanded) {
                      newExpanded.delete(phaseIndex);
                      if (editingPhaseName === phaseIndex) {
                        updatePhase(phaseIndex, "name", tempValue);
                        setEditingPhaseName(null);
                        setTempValue("");
                      }
                    } else {
                      newExpanded.add(phaseIndex);
                    }
                    setExpandedAccordions(newExpanded);
                  }}
                  sx={{
                    background: isDark ? "#1C1E20" : "#F3F6FA",
                    color: isDark ? "#ECEFF3" : "#434950",
                    boxShadow: isDark
                      ? "0px 4px 2.15px rgba(0,0,0,0.25)"
                      : "0px 1px 3px rgba(0,0,0,0.08)",
                    border: "1px solid",
                    borderColor: isDark ? "#31363B" : "#F3F6FA",
                    borderRadius: "16px !important",
                    mb: "12px",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    sx={{
                      minHeight: "64px",
                      borderRadius: "16px",
                      px: "16px",
                      "&:hover": { backgroundColor: "transparent" },
                      "&.Mui-focusVisible": { backgroundColor: "transparent" },
                      "& .MuiAccordionSummary-content": {
                        alignItems: isExpanded ? "flex-start" : "center",
                        justifyContent: "space-between",
                        overflow: "hidden",
                        my: "14px",
                      },
                      "& .MuiAccordionSummary-expandIconWrapper": {
                        order: -1,
                        marginRight: "8px",
                        marginLeft: 0,
                        alignSelf: isExpanded ? "flex-start" : "center",
                        marginTop: isExpanded ? "20px" : 0,
                        transform: "none !important",
                        "& svg": { width: "20px", height: "20px" },
                      },
                    }}
                    expandIcon={
                      isExpanded ? (
                        <ChevronDown
                          sx={{ color: isDark ? "#ECEFF3" : "#7B7B7B" }}
                        />
                      ) : (
                        <ChevronRight
                          sx={{ color: isDark ? "#ECEFF3" : "#7B7B7B" }}
                        />
                      )
                    }
                  >
                    {editingPhaseName === phaseIndex ? (
                      // Phase name edit state: rgba(255,255,255,0.1) bg + #555 border
                      <TextField
                        fullWidth
                        multiline
                        value={tempValue}
                        placeholder='Title'
                        onChange={e => setTempValue(e.target.value)}
                        onBlur={() => {
                          updatePhase(phaseIndex, "name", tempValue);
                          setEditingPhaseName(null);
                          setTempValue("");
                        }}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        autoFocus
                        variant='outlined'
                        size='small'
                        sx={{
                          mr: "13px",
                          "& .MuiOutlinedInput-root": {
                            color: isDark ? "#ECEFF3" : "#434950",
                            fontFamily: "Inter",
                            fontSize: "16px",
                            fontWeight: 600,
                            lineHeight: "24px",
                            letterSpacing: "-0.3125px",
                            background: isDark
                              ? "rgba(255,255,255,0.1)"
                              : "#FFFFFF",
                            borderRadius: "10px",
                            padding: 0,
                            "& fieldset": {
                              borderColor: isDark ? "#555555" : "#D5D9DF",
                              borderWidth: "1px",
                            },
                            "&:hover fieldset": {
                              borderColor: isDark ? "#555555" : "#D5D9DF",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: isDark ? "#555555" : "#D5D9DF",
                              borderWidth: "1px",
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            fontSize: "16px",
                            fontFamily: "Inter",
                            fontWeight: 600,
                            lineHeight: "24px",
                            letterSpacing: "-0.3125px",
                            padding: "8px 16px",
                            "&::placeholder": {
                              fontStyle: "italic",
                              opacity: 1,
                              color: "#8C8C8C",
                            },
                          },
                        }}
                      />
                    ) : (
                      // Phase name read state: no bg; hover (when expanded): rgba(255,255,255,0.1)
                      <Box sx={{ mr: "13px", flex: 1, overflow: "hidden" }}>
                        <Typography
                          onClick={e => {
                            if (isExpanded) {
                              e.stopPropagation();
                              setEditingPhaseName(phaseIndex);
                              setTempValue(phase.name ?? "");
                            }
                          }}
                          sx={{
                            color: phase.name
                              ? isDark
                                ? "#ECEFF3"
                                : "#434950"
                              : "#8C8C8C",
                            fontFamily: "Inter",
                            fontSize: "16px",
                            fontWeight: isExpanded ? 600 : 500,
                            lineHeight: "24px",
                            letterSpacing: "-0.3125px",
                            fontStyle: phase.name ? "normal" : "italic",
                            cursor: "pointer",
                            height: "36px",
                            width: "fit-content",
                            borderRadius: "10px",
                            "&:hover": isExpanded
                              ? {
                                  bgcolor: isDark
                                    ? "rgba(255,255,255,0.1)"
                                    : "rgba(213,217,223,0.5)",
                                }
                              : {},
                            display: "flex",
                            alignItems: "center",
                            padding: "8px 16px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {phase.name || "Title"}
                        </Typography>
                      </Box>
                    )}
                    {isExpanded && procedureSkeleton.phases.length > 1 && (
                      <IconButton
                        size='small'
                        onClick={e => {
                          e.stopPropagation();
                          deletePhase(phaseIndex);
                        }}
                        sx={{
                          color: isDark ? "#ECEFF3" : "#434950",
                          height: "32px",
                          width: "32px",
                          borderRadius: "8px",
                          alignSelf: isExpanded ? "flex-start" : "center",
                          background: isDark
                            ? "linear-gradient(135deg, rgba(67,73,80,0.8) 0%, rgba(49,54,59,0.8) 100%)"
                            : "#F3F6FA",
                          border: isDark
                            ? "1px solid rgba(106,112,121,0.2)"
                            : "none",
                          "&:hover": {
                            background: isDark
                              ? "linear-gradient(135deg, rgba(67,73,80,0.8) 0%, rgba(49,54,59,0.8) 100%)"
                              : "#F3F6FA",
                          },
                        }}
                      >
                        <Trash2 sx={{ fontSize: "16px" }} />
                      </IconButton>
                    )}
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: "20px", pb: "20px", pt: 0 }}>
                    {/* Separator between header and body */}
                    <Box
                      sx={{
                        borderTop: "1px solid",
                        borderColor: isDark ? "#393939" : "#E5E7EB",
                        mb: "16px",
                      }}
                    />

                    <TextField
                      multiline
                      value={
                        editingPhase === phaseIndex
                          ? tempValue
                          : phase.phase_summary || ""
                      }
                      placeholder='Description'
                      onChange={e => setTempValue(e.target.value)}
                      onClick={() => {
                        if (editingPhase !== phaseIndex) {
                          setEditingPhase(phaseIndex);
                          setTempValue(phase.phase_summary ?? "");
                        }
                      }}
                      onBlur={() => {
                        if (editingPhase === phaseIndex) {
                          updatePhase(phaseIndex, "phase_summary", tempValue);
                          setEditingPhase(null);
                          setTempValue("");
                        }
                      }}
                      variant='outlined'
                      size='small'
                      InputProps={{ readOnly: editingPhase !== phaseIndex }}
                      sx={{
                        width: "calc(100% - 55px)",
                        mr: "55px",
                        "& .MuiOutlinedInput-root": {
                          color: isDark ? "#ECEFF3" : "#434950",
                          fontFamily: "Inter",
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: "24px",
                          background: isDark ? "#323436" : "#FFFFFF",
                          borderRadius: "10px",
                          padding: "6px 15px",
                          cursor:
                            editingPhase === phaseIndex ? "text" : "pointer",
                          "& fieldset": {
                            borderColor:
                              editingPhase === phaseIndex
                                ? isDark
                                  ? "#555555"
                                  : "#D5D9DF"
                                : "transparent",
                            borderWidth: "1px",
                          },
                          "&:hover fieldset": {
                            borderColor:
                              editingPhase === phaseIndex
                                ? isDark
                                  ? "#555555"
                                  : "#D5D9DF"
                                : "transparent",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: isDark ? "#555555" : "#D5D9DF",
                            borderWidth: "1px",
                          },
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "0",
                          fontFamily: "Inter",
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: "24px",
                          cursor:
                            editingPhase === phaseIndex ? "text" : "pointer",
                          "&::placeholder": {
                            fontStyle: "italic",
                            opacity: 1,
                            color: "#8C8C8C",
                          },
                        },
                      }}
                    />

                    <List sx={{ mt: "16px", p: 0 }}>
                      {phase.phase_outline.map((item, itemIndex) => (
                        <ListItem
                          key={itemIndex}
                          sx={{
                            mb: "12px",
                            display: "flex",
                            alignItems: "flex-start",
                            padding: 0,
                            "& .MuiListItemSecondaryAction-root": {
                              top: "4px",
                              transform: "none",
                            },
                          }}
                          secondaryAction={
                            <IconButton
                              size='small'
                              edge='end'
                              onClick={() => deleteItem(phaseIndex, itemIndex)}
                              sx={{
                                color: isDark ? "#7B7B7B" : "#7B7B7B",
                                height: "28px",
                                width: "28px",
                                "&:hover": {
                                  bgcolor: "transparent",
                                },
                              }}
                            >
                              <Trash2 sx={{ fontSize: "16px" }} />
                            </IconButton>
                          }
                        >
                          <Box
                            sx={{
                              height: "6px",
                              width: "6px",
                              borderRadius: "50%",
                              mr: "12px",
                              mt: "13px",
                              flexShrink: 0,
                              bgcolor: "#7B7B7B",
                            }}
                          />
                          <TextField
                            multiline
                            value={
                              editingItem?.phaseIndex === phaseIndex &&
                              editingItem?.itemIndex === itemIndex
                                ? tempValue
                                : item || ""
                            }
                            placeholder='Item'
                            onChange={e => setTempValue(e.target.value)}
                            onClick={() => {
                              if (
                                editingItem?.phaseIndex !== phaseIndex ||
                                editingItem?.itemIndex !== itemIndex
                              ) {
                                setEditingItem({ phaseIndex, itemIndex });
                                setTempValue(item ?? "");
                              }
                            }}
                            onBlur={() => {
                              if (
                                editingItem?.phaseIndex === phaseIndex &&
                                editingItem?.itemIndex === itemIndex
                              ) {
                                updateItem(
                                  editingItem.phaseIndex,
                                  editingItem.itemIndex,
                                  tempValue,
                                );
                                setEditingItem(null);
                                setTempValue("");
                              }
                            }}
                            variant='outlined'
                            size='small'
                            InputProps={{
                              readOnly: !(
                                editingItem?.phaseIndex === phaseIndex &&
                                editingItem?.itemIndex === itemIndex
                              ),
                            }}
                            sx={{
                              flex: 1,
                              mr: "55px",
                              "& .MuiOutlinedInput-root": {
                                color: isDark ? "#ECEFF3" : "#434950",
                                fontFamily: "Inter",
                                fontSize: "14px",
                                fontWeight: 400,
                                lineHeight: "24px",
                                background: isDark
                                  ? "rgba(255,255,255,0.1)"
                                  : "#FFFFFF",
                                borderRadius: "10px",
                                padding: "6px 15px",
                                cursor:
                                  editingItem?.phaseIndex === phaseIndex &&
                                  editingItem?.itemIndex === itemIndex
                                    ? "text"
                                    : "pointer",
                                "& fieldset": {
                                  borderColor:
                                    editingItem?.phaseIndex === phaseIndex &&
                                    editingItem?.itemIndex === itemIndex
                                      ? isDark
                                        ? "#555555"
                                        : "#D5D9DF"
                                      : "transparent",
                                  borderWidth: "1px",
                                },
                                "&:hover fieldset": {
                                  borderColor:
                                    editingItem?.phaseIndex === phaseIndex &&
                                    editingItem?.itemIndex === itemIndex
                                      ? isDark
                                        ? "#555555"
                                        : "#D5D9DF"
                                      : "transparent",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor:
                                    editingItem?.phaseIndex === phaseIndex &&
                                    editingItem?.itemIndex === itemIndex
                                      ? isDark
                                        ? "#555555"
                                        : "#D5D9DF"
                                      : "transparent",
                                  borderWidth: "1px",
                                },
                              },
                              "& .MuiOutlinedInput-input": {
                                padding: "0",
                                fontFamily: "Inter",
                                fontSize: "14px",
                                fontWeight: 400,
                                lineHeight: "24px",
                                cursor:
                                  editingItem?.phaseIndex === phaseIndex &&
                                  editingItem?.itemIndex === itemIndex
                                    ? "text"
                                    : "pointer",
                                "&::placeholder": {
                                  fontStyle: "italic",
                                  opacity: 1,
                                  color: "#8C8C8C",
                                },
                              },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    <Button
                      startIcon={<CirclePlus sx={{ fontSize: "18px" }} />}
                      onClick={() => addItem(phaseIndex)}
                      sx={addBtnSx}
                    >
                      Add Item
                    </Button>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              background: isDark ? "#191A1B" : "#F7F9FB",
              borderTop: "1px solid",
              borderColor: isDark ? "#31363B" : "rgba(213,217,223,0.2)",
              height: "67px",
              px: "24px",
              pt: "17px",
              flexShrink: 0,
            }}
          >
            <Button
              startIcon={<CirclePlus sx={{ fontSize: "18px" }} />}
              onClick={addPhase}
              sx={addBtnSx}
            >
              Add Section
            </Button>

            <Button
              onClick={resetEditingState}
              sx={{
                ml: "auto",
                mr: "8px",
                height: "36px",
                color: isDark ? "#FFFFFF" : "#434950",
                fontFamily: "Inter",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "20px",
                letterSpacing: "0.021px",
                textTransform: "none",
                background: isDark
                  ? "linear-gradient(155.5deg, rgba(168,174,182,0.5) 0%, rgba(126,132,141,0.5) 100%)"
                  : "linear-gradient(156.04deg, rgba(248,249,250,0.8) 0%, rgba(226,231,235,0.9) 50%, rgb(211,218,225) 100%)",
                border: "1px solid",
                borderColor: isDark ? "rgba(168,174,182,0.5)" : "#D5D9DF",
                borderRadius: "12px",
                padding: "8px 16px",
                "&:hover": { opacity: 0.85 },
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={() => onSave(procedureSkeleton)}
              sx={{
                height: "36px",
                color: "#FFFFFF",
                fontFamily: "Inter",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "20px",
                letterSpacing: "0.021px",
                textTransform: "none",
                background:
                  "linear-gradient(151.75deg, rgb(20,110,55) 0%, rgb(26,135,68) 100%)",
                border: "none",
                borderRadius: isDark ? "10px" : "12px",
                padding: "8px 16px",
                "&:hover": { opacity: 0.85 },
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </TabContentOverlay>
  );
}
